import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/ui';
import { useMe } from '@/features/auth/session-context';
import { guidesFor } from '@/features/shell/help-content';
import { PageContainer } from '@/layouts/PageContainer';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import { ReportProblem } from './ReportProblem';
import styles from './shared.module.css';

/**
 * Help (MIG-04, #3): short how-to guides with search and an EN/BM switch.
 * Blueprint guide content follows the currently implemented flows.
 */
export function HelpPage() {
  const me = useMe();
  const { t } = useTranslation();
  const [q, setQ] = useUrlState('q', '', undefined, { replace: true });
  const [lang, setLang] = useUrlState<'en' | 'bm'>('lang', 'en', ['en', 'bm']);
  const [openGuide, setOpenGuide] = useOptionalUrlState('guide');
  const guides = guidesFor(me.activeRole.key).filter((g) =>
    [
      lang === 'en' ? g.title : g.titleBm,
      ...(lang === 'en' ? g.steps : g.stepsBm),
    ]
      .join(' ')
      .toLowerCase()
      .includes(q.trim().toLowerCase()),
  );

  return (
    <PageContainer>
      <div className={styles.helpStack}>
        {/* RL-1820: any staff member reports a problem from Help. */}
        <ReportProblem />
        <div>
          <div className={styles.helpBar}>
            <input
              className={styles.helpSearch}
              aria-label={t('help.search')}
              placeholder={lang === 'en' ? t('help.search') : 'Cari bantuan'}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div
              className={styles.seg}
              role="radiogroup"
              aria-label={t('help.language')}
            >
              {(['en', 'bm'] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  role="radio"
                  aria-checked={lang === l}
                  onClick={() => setLang(l)}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          {guides.length === 0 ? (
            <EmptyState title={t('help.noMatches')} />
          ) : (
            <ul className={styles.guides} aria-label="Guides">
              {guides.map((g) => (
                <li key={g.id}>
                  <button
                    type="button"
                    className={styles.guide}
                    aria-expanded={openGuide === g.id}
                    onClick={() =>
                      setOpenGuide(openGuide === g.id ? null : g.id)
                    }
                  >
                    <span>{lang === 'en' ? g.title : g.titleBm}</span>
                    <span className={styles.minutes}>{g.minutes} min</span>
                  </button>
                  {openGuide === g.id && (
                    <div className={styles.guideBody}>
                      <ol>
                        {(lang === 'en' ? g.steps : g.stepsBm).map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
